import { apiClient } from './client';
import { createCrudApi, type ActivityItem } from './crud';
import type { ApiSuccess } from '../types';

export type DealerStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'suspended' | 'inactive';

export interface Dealer {
  id: number;
  dealerCode: string;
  dealerName: string;
  legalName: string | null;
  dealerType: string;
  gstNumber: string | null;
  panNumber: string | null;
  contactPerson: string | null;
  email: string | null;
  mobile: string | null;
  regionId: number | null;
  stateId: number | null;
  creditLimit: number;
  creditDays: number;
  bankAccountName: string | null;
  bankAccountNumber: string | null;
  bankIfsc: string | null;
  registrationSource: string;
  status: DealerStatus;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: number | null;
  rejectionReason: string | null;
  linkedUserId: number | null;
  hasLogin: boolean;
  createdAt: string;
  temporaryPassword?: string;
  profileStatus?: 'complete' | 'incomplete_details' | 'incomplete_documents';
  detailsComplete?: boolean;
  documentsComplete?: boolean;
}

export interface CreateDealerInput {
  dealerName: string;
  legalName?: string;
  dealerType?: string;
  gstNumber?: string;
  panNumber?: string;
  contactPerson?: string;
  email?: string;
  mobile?: string;
  regionId?: number | null;
  stateId?: number | null;
  creditLimit?: number;
  creditDays?: number;
  bankAccountName?: string;
  bankAccountNumber?: string;
  bankIfsc?: string;
}

export interface DealerDocument {
  id: number;
  docType: string;
  fileName: string;
  fileUrl: string;
  version: number;
  isCurrent: boolean;
  createdAt: string;
}

export interface DealerAddress {
  id: number;
  addressType: string;
  label: string | null;
  line1: string;
  line2: string | null;
  city: string | null;
  stateId: number | null;
  regionId: number | null;
  pincode: string | null;
  contactPerson: string | null;
  contactMobile: string | null;
  latitude: number | null;
  longitude: number | null;
  isPrimary: boolean;
  status: string;
  createdAt: string;
}

export interface CreateAddressInput {
  addressType?: string;
  label?: string;
  line1: string;
  line2?: string;
  city?: string;
  stateId?: number | null;
  regionId?: number | null;
  pincode?: string;
  contactPerson?: string;
  contactMobile?: string;
  isPrimary?: boolean;
}

export interface DealerStatusHistory {
  id: number;
  fromStatus: string | null;
  toStatus: string;
  action: string;
  remarks: string | null;
  actorUserId: number | null;
  createdAt: string;
}

export interface DealerDashboard {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  pendingApproval: number;
  approved: number;
  drafts: number;
  rejected: number;
  creditExposure: number;
  recent: Dealer[];
}

const base = createCrudApi<Dealer, CreateDealerInput>('dealers');

export const dealersApi = {
  ...base,
  async dashboard(): Promise<DealerDashboard> {
    const { data } = await apiClient.get<ApiSuccess<DealerDashboard>>('/dealers/dashboard');
    return data.data;
  },
  async submit(id: number, remarks?: string): Promise<Dealer> {
    const { data } = await apiClient.post<ApiSuccess<Dealer>>(`/dealers/${id}/submit`, { remarks });
    return data.data;
  },
  async approve(id: number, remarks?: string): Promise<Dealer> {
    const { data } = await apiClient.post<ApiSuccess<Dealer>>(`/dealers/${id}/approve`, { remarks });
    return data.data;
  },
  async reject(id: number, reason: string): Promise<Dealer> {
    const { data } = await apiClient.post<ApiSuccess<Dealer>>(`/dealers/${id}/reject`, { reason });
    return data.data;
  },
  async setStatus(id: number, status: 'suspended' | 'inactive' | 'approved', remarks?: string): Promise<Dealer> {
    const { data } = await apiClient.post<ApiSuccess<Dealer>>(`/dealers/${id}/status`, { status, remarks });
    return data.data;
  },
  async provisionLogin(id: number, input: { email?: string; fullName?: string } = {}): Promise<{ dealer: Dealer; temporaryPassword: string }> {
    const { data } = await apiClient.post<ApiSuccess<{ dealer: Dealer; temporaryPassword: string }>>(`/dealers/${id}/provision-login`, input);
    return data.data;
  },
  async listDocuments(id: number): Promise<DealerDocument[]> {
    const { data } = await apiClient.get<ApiSuccess<DealerDocument[]>>(`/dealers/${id}/documents`);
    return data.data;
  },
  async addDocument(id: number, input: { docType: string; fileName: string; fileUrl: string }): Promise<DealerDocument> {
    const { data } = await apiClient.post<ApiSuccess<DealerDocument>>(`/dealers/${id}/documents`, input);
    return data.data;
  },
  async listAddresses(id: number): Promise<DealerAddress[]> {
    const { data } = await apiClient.get<ApiSuccess<DealerAddress[]>>(`/dealers/${id}/addresses`);
    return data.data;
  },
  async addAddress(id: number, input: CreateAddressInput): Promise<DealerAddress> {
    const { data } = await apiClient.post<ApiSuccess<DealerAddress>>(`/dealers/${id}/addresses`, input);
    return data.data;
  },
  async updateAddress(id: number, addressId: number, input: Partial<CreateAddressInput> & { status?: string }): Promise<DealerAddress> {
    const { data } = await apiClient.put<ApiSuccess<DealerAddress>>(`/dealers/${id}/addresses/${addressId}`, input);
    return data.data;
  },
  async removeAddress(id: number, addressId: number): Promise<void> {
    await apiClient.delete(`/dealers/${id}/addresses/${addressId}`);
  },
  async history(id: number): Promise<DealerStatusHistory[]> {
    const { data } = await apiClient.get<ApiSuccess<DealerStatusHistory[]>>(`/dealers/${id}/history`);
    return data.data;
  },
  async activity(id: number): Promise<ActivityItem[]> {
    return base.activity(id);
  },
};
